/**
 * Space Templates Configuration
 *
 * Defines predefined space templates with their feature presets.
 * Users can select a template and optionally customize its features.
 */

export interface SpaceFeatures {
  // Core features
  showCompletionCheckbox: boolean; // Show "done" checkbox
  showDueDate: boolean; // Show due date picker
  showPriority: boolean; // Show priority selector
  showTags: boolean; // Show tag editor

  // View options
  defaultView: 'list' | 'grid' | 'calendar' | 'kanban';
  sortBy: 'createdAt' | 'updatedAt' | 'title' | 'dueDate' | 'priority' | 'order';
  groupBy?: 'none' | 'status' | 'priority' | 'tags' | 'dueDate';

  // Filters
  showCompleted: boolean; // Show completed items by default
  defaultFilters?: {
    hideCompleted?: boolean;
    showOnlyOverdue?: boolean;
    [key: string]: any;
  };

  // Custom fields for metadata
  customFields?: CustomField[];
}

export interface CustomField {
  id: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'checkbox' | 'url';
  placeholder?: string;
  options?: string[]; // For select type
  required?: boolean;
  defaultValue?: any;
}

export interface SpaceTemplate {
  id: string;
  label: string;
  icon: string;
  description: string;
  color: string;
  features: SpaceFeatures;
  isBuiltIn: boolean;
}

/**
 * Predefined space templates
 */
export const SPACE_TEMPLATES: Record<string, SpaceTemplate> = {
  notes: {
    id: 'notes',
    label: 'Notes',
    icon: '📝',
    description: 'General note-taking and documentation',
    color: '#4A90E2',
    isBuiltIn: true,
    features: {
      showCompletionCheckbox: false, // ❌ No "done" checkbox
      showDueDate: false,
      showPriority: false,
      showTags: true,
      defaultView: 'list',
      sortBy: 'updatedAt',
      showCompleted: true,
    },
  },

  calendar: {
    id: 'calendar',
    label: 'Calendar',
    icon: '📅',
    description: 'Events, meetings, and schedules',
    color: '#F5A623',
    isBuiltIn: true,
    features: {
      showCompletionCheckbox: false,
      showDueDate: true, // Actually "event date/time"
      showPriority: false,
      showTags: true,
      defaultView: 'calendar',
      sortBy: 'dueDate',
      showCompleted: true,
      customFields: [
        {
          id: 'location',
          label: 'Location',
          type: 'text',
          placeholder: 'Where is this event?',
        },
        {
          id: 'url',
          label: 'Video Call Link',
          type: 'url',
          placeholder: 'https://meet.google.com/...',
        },
      ],
    },
  },

  todos: {
    id: 'todos',
    label: 'To-Do Lists',
    icon: '✅',
    description: 'Task management with completion tracking',
    color: '#7ED321',
    isBuiltIn: true,
    features: {
      showCompletionCheckbox: true, // ✅ Show "done" checkbox
      showDueDate: true,
      showPriority: true,
      showTags: true,
      defaultView: 'list',
      sortBy: 'dueDate',
      groupBy: 'priority',
      showCompleted: false, // Hide completed by default
      defaultFilters: {
        hideCompleted: true,
      },
    },
  },

  groceries: {
    id: 'groceries',
    label: 'Groceries',
    icon: '🛒',
    description: 'Shopping lists with categories',
    color: '#50E3C2',
    isBuiltIn: true,
    features: {
      showCompletionCheckbox: true, // ✅ Check off items as you shop
      showDueDate: false,
      showPriority: false,
      showTags: true, // For categories like "Produce", "Dairy"
      defaultView: 'list',
      sortBy: 'order', // Manual ordering
      groupBy: 'tags', // Group by category
      showCompleted: true, // Show checked items (crossed out)
      customFields: [
        {
          id: 'quantity',
          label: 'Quantity',
          type: 'text',
          placeholder: 'e.g., 2 lbs, 1 gallon',
        },
        {
          id: 'store',
          label: 'Store',
          type: 'text',
          placeholder: 'Which store?',
        },
      ],
    },
  },

  recipes: {
    id: 'recipes',
    label: 'Recipes',
    icon: '🍳',
    description: 'Cooking recipes and meal planning',
    color: '#F8E71C',
    isBuiltIn: true,
    features: {
      showCompletionCheckbox: false,
      showDueDate: false,
      showPriority: false,
      showTags: true, // For cuisine types, dietary restrictions
      defaultView: 'grid',
      sortBy: 'title',
      showCompleted: true,
      customFields: [
        {
          id: 'prepTime',
          label: 'Prep Time',
          type: 'text',
          placeholder: 'e.g., 15 minutes',
        },
        {
          id: 'servings',
          label: 'Servings',
          type: 'number',
          placeholder: '4',
        },
        {
          id: 'source',
          label: 'Recipe Source',
          type: 'url',
          placeholder: 'https://...',
        },
      ],
    },
  },

  travel: {
    id: 'travel',
    label: 'Travel',
    icon: '✈️',
    description: 'Trip planning and itineraries',
    color: '#BD10E0',
    isBuiltIn: true,
    features: {
      showCompletionCheckbox: true, // For packing lists, bookings
      showDueDate: true, // Travel dates
      showPriority: false,
      showTags: true, // For trip categories
      defaultView: 'list',
      sortBy: 'dueDate',
      groupBy: 'tags',
      showCompleted: false,
      defaultFilters: {
        hideCompleted: true,
      },
      customFields: [
        {
          id: 'location',
          label: 'Location',
          type: 'text',
          placeholder: 'Destination',
        },
        {
          id: 'confirmationNumber',
          label: 'Confirmation #',
          type: 'text',
          placeholder: 'Booking reference',
        },
      ],
    },
  },

  projects: {
    id: 'projects',
    label: 'Projects',
    icon: '📊',
    description: 'Project planning and tracking',
    color: '#D0021B',
    isBuiltIn: true,
    features: {
      showCompletionCheckbox: true,
      showDueDate: true,
      showPriority: true,
      showTags: true,
      defaultView: 'kanban', // Columns: To-Do, In Progress, Done
      sortBy: 'priority',
      groupBy: 'status',
      showCompleted: true,
      customFields: [
        {
          id: 'status',
          label: 'Status',
          type: 'select',
          options: ['To-Do', 'In Progress', 'Blocked', 'Done'],
          defaultValue: 'To-Do',
        },
        {
          id: 'assignee',
          label: 'Assignee',
          type: 'text',
          placeholder: 'Who is responsible?',
        },
      ],
    },
  },

  custom: {
    id: 'custom',
    label: 'Custom',
    icon: '📋',
    description: 'Create your own space type',
    color: '#9013FE',
    isBuiltIn: false,
    features: {
      // Start with basic defaults
      showCompletionCheckbox: false,
      showDueDate: false,
      showPriority: false,
      showTags: true,
      defaultView: 'list',
      sortBy: 'createdAt',
      showCompleted: true,
    },
  },
};

/**
 * Get all built-in templates (excludes custom)
 */
export function getBuiltInTemplates(): SpaceTemplate[] {
  return Object.values(SPACE_TEMPLATES).filter((t) => t.isBuiltIn);
}

/**
 * Get template by ID
 */
export function getTemplate(id: string): SpaceTemplate | undefined {
  return SPACE_TEMPLATES[id];
}

/**
 * Get template label for display
 */
export function getTemplateLabel(typeId: string, customLabel?: string): string {
  const template = SPACE_TEMPLATES[typeId];

  if (typeId === 'custom' && customLabel) {
    return customLabel;
  }

  return template?.label || customLabel || 'Space';
}

/**
 * Get template icon
 * Prefers template icon over custom icon to ensure consistency
 * Only uses custom icon if it's explicitly set and valid
 */
export function getTemplateIcon(typeId: string, customIcon?: string): string {
  const template = SPACE_TEMPLATES[typeId];

  // Always prefer the template icon for built-in templates
  // Only use custom icon if it's set AND we're using the 'custom' template
  if (template?.icon && typeId !== 'custom') {
    return template.icon;
  }

  // For custom templates, use the custom icon if provided
  return customIcon || template?.icon || '📋';
}

/**
 * Get default features for a template
 */
export function getDefaultFeatures(templateId: string): SpaceFeatures {
  const template = SPACE_TEMPLATES[templateId] || SPACE_TEMPLATES.custom;
  return { ...template.features }; // Return a copy
}

/**
 * Validate custom fields
 */
export function validateCustomField(field: CustomField): boolean {
  if (!field.id || !field.label || !field.type) {
    return false;
  }

  if (field.type === 'select' && (!field.options || field.options.length === 0)) {
    return false;
  }

  return true;
}
